import { Image } from '@chakra-ui/react';
import classNames from 'classnames';
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import arrowBack from 'assets/img/icons/chevron-back.svg';

import styles from './styles.module.scss';

interface RenderControlsParams {
  prevControlHandler: () => void;
  nextControlHandler: () => void;
}

type RenderControls = (params: RenderControlsParams) => ReactNode;

interface Props extends PropsWithChildren {
  scrollBy: number;
  viewportItemsCount?: number;
  itemsCount?: number;
  showControls?: boolean;
  onScroll?: (element: HTMLElement) => void;
  scrollType?: 'free' | 'fixed';
  renderControls?: RenderControls;
}

const Carousel: FC<Props> = ({
  scrollBy,
  showControls = true,
  viewportItemsCount,
  itemsCount,
  children,
  onScroll,
  scrollType = 'free',
  renderControls,
}) => {
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState<number>(0);
  const [showPrevArrow, setShowPrevArrow] = useState<boolean>(false);
  const [showNextArrow, setShowNextArrow] = useState<boolean>(true);

  const scrollToPage = (next: number): void => {
    if (scrollEl === null) return;
    setPage(next);
    scrollEl.scrollTo({
      behavior: 'smooth',
      left: next * scrollBy,
    });
  };

  const scrollTo = (by: number, dir: 'left' | 'right'): void => {
    if (scrollEl === null) return;
    let left;
    if (dir === 'left') {
      left = scrollEl.scrollLeft - by;
      if (left < 0) {
        left = scrollEl.scrollWidth;
      }
    } else {
      if (scrollEl.scrollLeft >= scrollEl.scrollWidth - scrollEl.clientWidth) {
        left = 0;
      } else {
        left = scrollEl.scrollLeft + by;
      }
    }
    scrollEl.scrollTo({
      behavior: 'smooth',
      left,
    });
  };

  const scrollListener = (): void => {
    if (scrollEl === null) return;
    setShowPrevArrow(scrollEl.scrollLeft > 0);
    setShowNextArrow(
      scrollEl.scrollLeft < scrollEl.scrollWidth - scrollEl.clientWidth
    );
    if (!onScroll) return;
    onScroll(scrollEl);
  };

  const onPrevControlClick = (): void => {
    if (scrollType === 'fixed' && itemsCount && viewportItemsCount) {
      let next = page - 1;
      if (next < 0) {
        next = itemsCount - viewportItemsCount;
      }
      scrollToPage(next);
    } else {
      scrollTo(scrollBy, 'left');
    }
  };

  const onNextControlClick = (): void => {
    if (scrollType === 'fixed' && itemsCount && viewportItemsCount) {
      let next = page + 1;
      if (next > itemsCount - viewportItemsCount) {
        next = 0;
      }
      scrollToPage(next);
    } else {
      scrollTo(scrollBy, 'right');
    }
  };

  useEffect(() => {
    if (scrollEl === null) return;
    scrollEl.addEventListener('scroll', scrollListener);

    return () => scrollEl.removeEventListener('scroll', scrollListener);
  }, [scrollEl]);

  let controls;
  if (showControls) {
    if (renderControls) {
      controls = renderControls({
        prevControlHandler: onPrevControlClick,
        nextControlHandler: onNextControlClick,
      });
    } else {
      controls = (
        <>
          {showPrevArrow && (
            <button
              className={classNames(
                styles.controlButton,
                styles.controlButton__left
              )}
              onClick={onPrevControlClick}
            >
              <Image
                width="10px"
                className={styles.arrow__left}
                src={arrowBack}
                alt=""
              />
            </button>
          )}

          {showNextArrow && (
            <button
              className={classNames(
                styles.controlButton,
                styles.controlButton__right
              )}
              onClick={onNextControlClick}
            >
              <Image
                width="10px"
                className={styles.arrow__right}
                src={arrowBack}
                alt=""
              />
            </button>
          )}
        </>
      );
    }
  }

  return (
    <div className={styles.root}>
      <div className={classNames(styles.scroll)} ref={setScrollEl}>
        {children}
      </div>
      {controls}
    </div>
  );
};

export default Carousel;
